import { PassengerType } from '@api/passengerTypes'
import { Filter, Sorting, CheckoutFormFields } from '@enums'
import makeApi from '@lib/api'
import { Colors } from '@lib/style'
import { TicketsAction } from '@queries/settings/enum'

interface BackgroundImageSetting {
  enabled: boolean
  headerOffset: boolean
  url: string | null
}

interface BookingDetailsSetting {
  showFareClass: boolean
}

interface BookedOutSetting {
  enabled: boolean
}

interface CheckoutSetting {
  type: 'old' | 'new' | 'express'
  widgetOrder?: Array<CheckoutFormFields>
  merchantName?: string
}

interface CheckoutPassengerEditSetting {
  allowCount: boolean
  allowType: boolean
}

type ColorsSetting = Colors

interface CurrencySetting {
  supported: Currency[]
  default: Currency
}

interface FareClassesSetting {
  displayOn: 'nowhere' | 'checkout' | 'search_results' | 'everywhere'
  supported: string[]
  split: boolean
}

interface LoadConnectionsSetting {
  enabled: boolean
  timeAllowance?: number
}

export interface CityLandingSetting {
  showPriceHint: boolean
}

interface FilterSetting {
  enabled: boolean
  directOnlyInlineHeaderView: boolean
  supported: Filter[]
  resetFilters: boolean
}

interface GovernmentIdSetting {
  trips: TripCondition
  types: GovernmentIdType[]
  default: GovernmentIdType | null
  extended?: boolean
  fields: GovernmentIdFields[]
}

interface GenderSettings {
  enabled: boolean
  trips: TripCondition
}

interface LocaleSetting {
  supported: Locale[]
  default: Locale
}

interface LogoSetting {
  url: string | null
  footerUrl: string | null
  redirectionUrl: string | null
}

interface PassengerTypeSetting {
  enabled: boolean
}

interface ReservationSetting {
  enabled: boolean
}

interface ReturnTripSetting {
  enabled: boolean
  mixedFareClasses: boolean
}

interface ReturnTimeSetting {
  enabled: boolean
}

interface ReturnTimeSetting {
  enabled: boolean
}

interface WeeklyTripSetting {
  enabled: boolean
  searchDays: number
  requiredDays: number
  returnTrip: boolean
}

interface ReservationSetting {
  enabled: boolean
}

interface QuickReservationSetting {
  enabled: boolean
}

interface CustomDomainSetting {
  enabled: boolean
}

interface AncillarySetting {
  enabled: boolean
  preselected: string[]
}

interface PassengerTypesListSetting {
  enabled: boolean
  carrier: string | null
  multiple: boolean
  crossCountry: boolean
  totalPaxLimit: number | null
  ageRanges: PassengerType[]
}

interface DiscountCodesSetting {
  enabled: boolean
  displayOn: 'checkout' | 'everywhere'
}

interface AmtrackSavedCardsSetting {
  enabled: boolean
  cvc: boolean
}

interface CarrierLogoSetting {
  enabled: boolean
  source: 'operating_carrier' | 'marketing_carrier'
}

interface PaxDiscountCodesSetting {
  enabled: boolean
  displayOn: 'checkout' | 'everywhere' | 'search_results'
}

interface SearchBarSettings {
  layout: Direction
}

interface SearchResultURLSetting {
  url: string
}

export interface SeatSelectionSetting {
  enabledOnSearchResults: boolean
  enabledOnCheckout: boolean
  flowType: 'single_step' | 'two_step'
  sendFareClass: boolean
  mandatory: boolean
  showSeatsPerSegmentOnCheckout: boolean
  showFareFeatures: boolean
  showPrice: boolean
  preselectSeats: boolean
}

interface SortingSetting {
  enabled: boolean
  supported: Sorting[]
  default: Sorting | null
}

export interface SiteNameSetting {
  name: string
  alternateName?: string[]
}

export interface OrganizationSetting {
  name: string
  alternateName?: string[]
  description?: string
  email?: string
  telephone?: string
  foundingDate?: string
  legalName?: string
  logo?: string
  image?: string
  sameAs?: string[]
  url?: string
  address?: {
    streetAddress?: string
    addressLocality?: string
    addressRegion?: string
    postalCode?: string
    addressCountry?: string
  }
}

interface TitleSetting {
  text: string
  favicon: string
  siteName?: SiteNameSetting | null
  organization?: OrganizationSetting | null
}

interface TripTimeSetting {
  enabled: boolean
}

interface CPFSettings {
  enabled: boolean
}

export type PaymentMethodSetting = {
  provider: PaymentProvider | null
  options?: {
    cvc?: boolean
    vaultId?: string
    cardExpirySplit?: boolean
    tokenizationStorage?: 'volatile' | undefined
    checkDetails?: boolean
    minPriceThreshold?: number
    maxPriceThreshold?: number
    minHoursToDeparture?: number
  }
} | null

export interface PaymentProvidersMethodsSetting {
  applePay: PaymentMethodSetting
  googlePay: PaymentMethodSetting
  creditCard: PaymentMethodSetting
  mp: PaymentMethodSetting
  pix: PaymentMethodSetting
  paypal: PaymentMethodSetting
  cash: PaymentMethodSetting
  terminal: PaymentMethodSetting
  invoice: PaymentMethodSetting
  ideal: PaymentMethodSetting
  dana: PaymentMethodSetting
  gcash: PaymentMethodSetting
  blik: PaymentMethodSetting
  storedPayment: PaymentMethodSetting
  ox: PaymentMethodSetting
  checkoutcomFlow: PaymentMethodSetting
  sepa_direct_debit: PaymentMethodSetting
}

interface BillingAddressSetting {
  enabled: boolean
}

interface HoldBookingSettings {
  enabled: boolean
  copyLink: boolean
}

interface DownloadTicketSetting {
  enabled: boolean
  action: TicketsAction
}

interface BookingNumbersSetting {
  distribusionNumber: boolean
  retailerNumber: boolean
  carrierNumber: boolean
}

interface MarketingMaterialsSetting {
  enabled: boolean
}

interface VehicleIdentifierSetting {
  enabled: boolean
}

interface CalendarSettings {
  enabled: boolean
  displayPrice: 'all_days' | 'today'
}

interface EmailSetting {
  enabled: boolean
  optional: boolean
  verification: boolean
}

interface EndpointsSetting {
  newBackend: string[]
}

interface WhatsappButtonSetting {
  enabled: boolean
  phoneNumber: string | null
}

interface TrackTripSetting {
  enabled: boolean
}

interface ManageBookingsSetting {
  enabled: boolean
  backgroundUrl: string | null
}

interface CustomElementsSetting {
  enabled: boolean
  pages: PageBlocks.Structure[]
}

interface NethoneSetting {
  enabled: boolean
}

interface MenuSettings {
  items: NavBar.Item[]
}

interface PriceCalendarSetting {
  enabled: boolean
}

interface AccommodationSetting {
  enabled: boolean
  partner: Partners
}

interface SupportSettings {
  url?: string | null
}

interface FooterMenuItem {
  urls: string[]
  icon?: string
}

interface FooterSocialItem {
  type: string
  url: string
}

interface FooterSettings {
  menu: FooterMenuItem[][]
  socials: FooterSocialItem[]
}

interface AgentCounterPageSetting {
  departureLocation: Location.Item | null
  arrivalLocation: Location.Item | null
}

interface TransportationModeSetting {
  enabled: boolean
  supported: Lowercase<Vehicle.Code>[]
}

interface SuggestionsSetting {
  display: Location.Type[]
  count: number
}

interface DiscountVoucherSetting {
  enabled: boolean
  dropdown?: boolean
}

export type SearchMode = 'simple' | 'advanced'

interface AdvancedSearchSetting {
  enabled: boolean
  default: SearchMode
  travelClass: boolean
  minTransferTime: {
    enabled: boolean
    intervalMinutes: number
    intervalCount: number
  }
  trainType: boolean
}

interface MediaSetting {
  enabled: boolean
  displayOn: 'search_results' | 'checkout' | 'everywhere'
  preview: boolean
}

interface ResellingSetting {
  enabled: boolean
  image: string | null
  link: string | null
}

interface NameSetting {
  enabled: boolean
}

interface TermsAndPrivacySetting {
  enabled: boolean
}

interface SeoSettings {
  enabled: boolean
}

export interface DistribusionBrandingSetting {
  enabled: boolean
  pages: string[]
  displayOn: 'header' | 'footer' | 'everywhere'
}

interface CookiesConsentSettings {
  enabled: boolean
  provider: CookiesConsentProvider
  configuration: { cbid: string } | null
}

interface ChangeCalculatorSetting {
  enabled: boolean
}

interface PriceHintsSetting {
  enabled: boolean
}

export interface ABTestSettings {
  enabled: boolean
  feature: AbTestFeature | null
}

interface InvoiceSetting {
  enabled: boolean
}

interface SeatProductSetting {
  enabled: boolean
}

interface JourneyCardSetting {
  view: 'detailed' | 'compact'
}

interface ResendConfirmationEmailSetting {
  enabled: boolean
}

interface PassengerDetailsSetting {
  mandatory: boolean
  dynamicPassengerData: boolean
}

interface ManageBookingHelpBannerSetting {
  enabled: boolean
  email: string | null
}

interface UserInterfaceSetting {
  date: { textInputEnabled: boolean }
  time: { textInputEnabled: boolean }
}

type StopOverFilterDefault = 'include' | 'exclude'

interface StopOverFilterSetting {
  enabled: boolean
  default: StopOverFilterDefault
  ruleSelection: boolean
}

interface OpenReturnSetting {
  enabled: boolean
}

interface MultistepCheckoutSetting {
  enabled: boolean
}

interface SubscriptionsSetting {
  cancellationEnabled: boolean
}

export interface SettingsResponse {
  discountVoucher: DiscountVoucherSetting
  backgroundImage: BackgroundImageSetting
  bookingDetails: BookingDetailsSetting
  bookingNumbers: BookingNumbersSetting
  bookedOut: BookedOutSetting
  checkout: CheckoutSetting
  checkoutPassengerEdit: CheckoutPassengerEditSetting
  colors: ColorsSetting
  loadConnections: LoadConnectionsSetting
  currency: CurrencySetting
  fareClasses: FareClassesSetting
  filter: FilterSetting
  governmentId: GovernmentIdSetting
  locale: LocaleSetting
  logo: LogoSetting
  passengerType: PassengerTypeSetting
  returnTrip: ReturnTripSetting
  returnStartTime: ReturnTimeSetting
  returnEndTime: ReturnTimeSetting
  searchBar: SearchBarSettings
  searchResultUrl: SearchResultURLSetting
  seatSelection: SeatSelectionSetting
  sorting: SortingSetting
  tripTime: TripTimeSetting
  cpf: CPFSettings
  paymentProvidersMethods: PaymentProvidersMethodsSetting
  reservation: ReservationSetting
  reservationCardCheck: ReservationSetting
  passengerTypesList: PassengerTypesListSetting
  discountCodes: DiscountCodesSetting
  paxDiscountCodes: PaxDiscountCodesSetting
  ancillaries: AncillarySetting
  customDomain: CustomDomainSetting
  quickReservation: QuickReservationSetting
  amtrackSavedCards: AmtrackSavedCardsSetting
  billingAddress: BillingAddressSetting
  carrierLogo: CarrierLogoSetting
  holdBooking: HoldBookingSettings
  downloadTicket: DownloadTicketSetting
  marketingMaterials: MarketingMaterialsSetting
  searchCalendar: CalendarSettings
  vehicleIdentifier: VehicleIdentifierSetting
  email: EmailSetting
  title: TitleSetting
  endpoints: EndpointsSetting
  whatsappButton: WhatsappButtonSetting
  gender: GenderSettings
  trackTrip: TrackTripSetting
  customElements: CustomElementsSetting
  manageBookings: ManageBookingsSetting
  nethone: NethoneSetting
  menu: MenuSettings
  priceCalendar: PriceCalendarSetting
  accommodation: AccommodationSetting
  footer: FooterSettings
  support: SupportSettings
  agentCounterPage: AgentCounterPageSetting
  transportationMode: TransportationModeSetting
  suggestions: SuggestionsSetting
  media: MediaSetting
  reselling: ResellingSetting
  passengerName: NameSetting
  termsAndPrivacy: TermsAndPrivacySetting
  seo: SeoSettings
  distribusionBranding: DistribusionBrandingSetting
  cookiesConsent: CookiesConsentSettings
  changeCalculator: ChangeCalculatorSetting
  priceHints: PriceHintsSetting
  abTest: ABTestSettings
  invoicing: InvoiceSetting
  weeklyTrip: WeeklyTripSetting
  seatProduct: SeatProductSetting
  journeyCard: JourneyCardSetting
  resendConfirmationEmail: ResendConfirmationEmailSetting
  passengerDetails: PassengerDetailsSetting
  manageBookingHelpBanner: ManageBookingHelpBannerSetting
  userInterface: UserInterfaceSetting
  stopOverFilter: StopOverFilterSetting
  openReturn: OpenReturnSetting
  multistepCheckout: MultistepCheckoutSetting
  advancedSearch: AdvancedSearchSetting
  subscription: SubscriptionsSetting
}

export interface SettingsRequest {
  marketingCarrierCode: string | null
  retailerPartnerNumber: number
}

const settingsApi = {
  load: makeApi.get<SettingsRequest, SettingsResponse>('/settings'),
}

export default settingsApi
